import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { authFetch } from '../../utils/authFetch';
import { BASE_URL } from '../../utils/config';
import LanguageDropdown from '../../components/LanguageDropdown';

const ChangePassword = ({ setHideFooter }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  useEffect(() => {
    setHideFooter(true);
    return () => {
      // Polling disabled to prevent property changes during animation
      setHideFooter(false);
    };
  }, [setHideFooter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.oldPassword.trim()) {
      newErrors.oldPassword = t('changePassword.validation.currentPasswordRequired');
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = t('changePassword.validation.newPasswordRequired');
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t('changePassword.validation.passwordMinLength');
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = t('changePassword.validation.confirmPasswordRequired');
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('changePassword.validation.passwordsDontMatch');
    }

    if (formData.oldPassword === formData.newPassword) {
      newErrors.newPassword = t('changePassword.validation.sameAsOld');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await authFetch(`${BASE_URL}/change-password`, {
        method: 'POST',
        body: JSON.stringify({
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || t('changePassword.success'), {
          autoClose: 3000,
          position: "bottom-right"
        });
        
        // Clear form
        setFormData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Redirect to profile or home
        setTimeout(() => {
          navigate('/ProfilePage');
        }, 2000);
      } else {
        toast.error(data.message || t('changePassword.error'));
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Error changing password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(2px)',
      WebkitBackdropFilter: 'blur(5px)'
    }}>
      <div style={{
        position: 'absolute',
        top: '15px',
        left: '15px',
        zIndex: 10
      }}>
        <LanguageDropdown currentLanguage={currentLanguage} setCurrentLanguage={setCurrentLanguage} />
      </div>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        maxWidth: '450px',
        width: '100%',
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        {/* Close Button */}
        <div style={{ textAlign: 'right', marginBottom: '10px' }}>
          <button
            onClick={() => navigate('/profile')}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#29B185',
              padding: '5px',
              borderRadius: '50%',
              width: '35px',
              height: '35px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            margin: '0 0 10px 0',
            color: '#333',
            fontSize: '28px',
            fontWeight: '600'
          }}>
            {t('changePassword.title')}
          </h2>
          <p style={{
            color: '#666',
            fontSize: '16px',
            lineHeight: '1.5'
          }}>
            {t('changePassword.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Current Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontWeight: '500'
            }}>
              {t('changePassword.currentPassword')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.oldPassword ? 'text' : 'password'}
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
                placeholder={t('changePassword.currentPassword')}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `2px solid ${errors.oldPassword ? '#ff4d4f' : '#e1e5e9'}`,
                  borderRadius: '6px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#29B185'}
                onBlur={(e) => e.target.style.borderColor = errors.oldPassword ? '#ff4d4f' : '#e1e5e9'}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('oldPassword')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '12px',
                  whiteSpace: 'nowrap'
                }}
              >
                {showPasswords.oldPassword ? t('changePassword.hidePassword') : t('changePassword.showPassword')}
              </button>
            </div>
            {errors.oldPassword && (
              <span style={{ color: '#e74c3c', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                {errors.oldPassword}
              </span>
            )}
          </div>

          {/* New Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontWeight: '500'
            }}>
              {t('changePassword.newPassword')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.newPassword ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder={t('changePassword.newPassword')}
                style={{
                  width: '100%',
                  padding: '12px 45px 12px 12px',
                  border: `2px solid ${errors.newPassword ? '#e74c3c' : '#e1e5e9'}`,
                  borderRadius: '6px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#29B185'}
                onBlur={(e) => e.target.style.borderColor = errors.newPassword ? '#e74c3c' : '#e1e5e9'}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('newPassword')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  fontSize: '18px'
                }}
              >
                {showPasswords.newPassword ? t('changePassword.hidePassword') : t('changePassword.showPassword')}
              </button>
            </div>
            {errors.newPassword && (
              <span style={{ color: '#e74c3c', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                {errors.newPassword}
              </span>
            )}
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontWeight: '500'
            }}>
              {t('changePassword.confirmPassword')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.confirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('changePassword.confirmPassword')}
                style={{
                  width: '100%',
                  padding: '12px 45px 12px 12px',
                  border: `2px solid ${errors.confirmPassword ? '#e74c3c' : '#e1e5e9'}`,
                  borderRadius: '6px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#29B185'}
                onBlur={(e) => e.target.style.borderColor = errors.confirmPassword ? '#e74c3c' : '#e1e5e9'}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirmPassword')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  fontSize: '18px'
                }}
              >
                {showPasswords.confirmPassword ? t('changePassword.hidePassword') : t('changePassword.showPassword')}
              </button>
            </div>
            {errors.confirmPassword && (
              <span style={{ color: '#e74c3c', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                {errors.confirmPassword}
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: '#29B185',
              color: 'white',
              padding: '12px 20px',
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              width: '100%',
              opacity: isLoading ? 0.7 : 1,
              transition: 'opacity 0.3s ease'
            }}
          >
            {isLoading ? t('common.saving') : t('changePassword.submitButton')}
          </button>
        </form>

        <div style={{ marginTop: '30px' }}>
          <Link 
            to="/profile" 
            style={{
              color: '#29B185',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            {t('changePassword.backToProfile')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
